/**
 * Lazy Motion re-exports.
 *
 * Central import point for framer-motion's tree-shakeable `m` components and
 * the `LazyMotion` provider. Using `m` instead of `motion` avoids pulling the
 * full ~32 kB feature bundle into every component — features are loaded once,
 * asynchronously, by the `<LazyMotion>` provider in `src/app/providers.tsx`.
 *
 * Migration status: incremental. Components that have been migrated import
 * `{ m }` from this module. Components still using `motion` from
 * 'framer-motion' work fine — `motion` self-bundles features, so they're
 * just heavier. Migrate them over time.
 *
 * @see https://motion.dev/docs/react-lazy-motion
 */

import { useEffect, useState } from 'react';
import {
  LazyMotion as FramerLazyMotion,
  m as framerM,
  AnimatePresence as FramerAnimatePresence,
  useReducedMotion as useFramerReducedMotion,
  useAnimationControls as useFramerAnimationControls,
} from 'framer-motion';

export const LazyMotion = FramerLazyMotion;
export const m = framerM;
export const AnimatePresence = FramerAnimatePresence;
export const useAnimationControls = useFramerAnimationControls;

const REDUCE_MOTION_ATTR = 'data-reduce-motion';

function readAppToggle(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute(REDUCE_MOTION_ATTR) === '1';
}

/**
 * useReducedMotion — ORs the OS-level `prefers-reduced-motion` media query
 * (via framer-motion's own hook) with the in-app "Reduce motion" toggle in
 * Settings → Appearance (`data-reduce-motion="1"` on <html>, set by
 * `src/features/account/routes/AppearanceRoute.tsx`). Either signal alone is
 * enough to return true — the app toggle is an escape hatch for users who
 * want motion off without touching their OS accessibility settings (e.g. on
 * a shared or managed machine), not a replacement for the OS signal.
 */
export function useReducedMotion(): boolean {
  const osPref = useFramerReducedMotion();
  const [appToggle, setAppToggle] = useState(readAppToggle);

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setAppToggle(readAppToggle());
    });
    observer.observe(target, { attributes: true, attributeFilter: [REDUCE_MOTION_ATTR] });
    return () => observer.disconnect();
  }, []);

  return Boolean(osPref) || appToggle;
}

/**
 * Async feature loader for `<LazyMotion>`. Loads `domMax` (layout + dom
 * animations) — required because several primitives (`Tabs`, `TabRail`,
 * `SegmentedControl`) use `layoutId`.
 *
 * Usage in providers.tsx:
 *   <LazyMotion features={loadMotionFeatures} strict>
 */
export const loadMotionFeatures = () => import('framer-motion').then((mod) => mod.domMax);
